import React, { useState } from 'react';
import { ApiKey } from '@/services/apiKeys.service';
import {
  Edit,
  Trash2,
  Copy,
  Search,
  CheckCircle,
  Eye,
  EyeOff,
  Code
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Switch } from "./ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { cn } from "@/lib/utils";
import { formatDate } from '@/lib/format';
import { useTranslation } from 'react-i18next';

interface ApiKeyDataTableProps {
  apiKeys: ApiKey[];
  isLoading: boolean;
  onEdit: (apiKey: ApiKey) => void;
  onDelete: (apiKey: ApiKey) => void;
  onToggleActive: (apiKey: ApiKey) => void;
  isTogglePending: boolean;
}

export const ApiKeyDataTable = ({
  apiKeys,
  isLoading,
  onEdit,
  onDelete,
  onToggleActive,
  isTogglePending
}: ApiKeyDataTableProps) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const filteredApiKeys = apiKeys.filter(apiKey =>
    apiKey.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (apiKey.description && apiKey.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const copyToClipboard = async (text: string, keyId: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(keyId);
      setTimeout(() => setCopiedKey(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const maskApiKey = (prefix: string, lastFour: string) => {
    return `${prefix}_${'*'.repeat(20)}${lastFour}`;
  };

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder={t('common:x1.searchApiKeys', 'API Keys suchen...')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 h-9 text-sm border-slate-200 focus-visible:ring-slate-200"
          />
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <Table>
          <TableHeader className="bg-slate-50">
            <TableRow className="hover:bg-transparent border-slate-200">
              <TableHead className="w-[200px] font-semibold text-slate-900 h-11">{t('common:x1.nameEnvironment', 'Name & Umgebung')}</TableHead>
              <TableHead className="font-semibold text-slate-900 h-11">API Key</TableHead>
              <TableHead className="w-[150px] font-semibold text-slate-900 h-11">{t('common:field.permissions', 'Berechtigungen')}</TableHead>
              <TableHead className="w-[120px] font-semibold text-slate-900 h-11">{t('common:field.status', 'Status')}</TableHead>
              <TableHead className="w-[150px] font-semibold text-slate-900 h-11">{t('common:x1.lastUsed', 'Letzte Nutzung')}</TableHead>
              <TableHead className="w-[100px] text-right font-semibold text-slate-900 h-11">{t('common:field.actions', 'Aktionen')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="h-32 text-center text-slate-500">
                  {t('common:state.loading', 'Laden...')}
                </TableCell>
              </TableRow>
            ) : filteredApiKeys.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="h-32 text-center">
                  <div className="flex flex-col items-center justify-center text-slate-500">
                    <Code className="h-8 w-8 mb-2 opacity-20" />
                    <p>{t('common:x1.noApiKeys', 'Keine API Keys gefunden')}</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              filteredApiKeys.map((apiKey) => (
                <TableRow key={apiKey.id} className="hover:bg-slate-50/50 border-slate-100 group">
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <span className="font-medium text-slate-900">{apiKey.name}</span>
                      <div className="flex gap-1">
                        <Badge variant="secondary" className="rounded-full bg-blue-50 text-blue-700 border-blue-100 px-1.5 py-0 text-[10px] font-semibold h-4">
                          {apiKey.prefix === 'sk_test' ? 'Test' : 'Live'}
                        </Badge>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <code className="text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1 font-mono text-slate-600">
                        {maskApiKey(apiKey.prefix, apiKey.last_four)}
                      </code>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => copyToClipboard(maskApiKey(apiKey.prefix, apiKey.last_four), apiKey.id)}
                        className="h-6 w-6 text-slate-400 hover:text-slate-900"
                        title={t('common:x1.copyKey', 'Key kopieren')}
                      >
                        {copiedKey === apiKey.id ? (
                          <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                        ) : (
                          <Copy className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {apiKey.permissions.read && (
                        <Badge className="rounded-full bg-emerald-50 text-emerald-700 border-emerald-100 px-1.5 py-0 text-[10px] font-semibold border">
                          Read
                        </Badge>
                      )}
                      {apiKey.permissions.write && (
                        <Badge className="rounded-full bg-orange-50 text-orange-700 border-orange-100 px-1.5 py-0 text-[10px] font-semibold border">
                          Write
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={apiKey.is_active}
                        onCheckedChange={() => onToggleActive(apiKey)}
                        disabled={isTogglePending}
                        className="scale-75 data-[state=checked]:bg-[#0F99FD]"
                      />
                      <span className={cn(
                        "text-xs font-medium",
                        apiKey.is_active ? "text-slate-700" : "text-slate-400"
                      )}>
                        {apiKey.is_active ? 'Aktiv' : 'Inaktiv'}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-500 text-xs">
                    {apiKey.last_used_at 
                      ? formatDate(apiKey.last_used_at) 
                      : 'Nie'}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onEdit(apiKey)}
                        className="h-8 w-8 text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onDelete(apiKey)}
                        className="h-8 w-8 text-slate-500 hover:text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
